import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useTenantBranding from '../hooks/useTenantBranding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { KeyRound, Loader2, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { branding } = useTenantBranding();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success('Login successful!');
        navigate('/');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  // Quick login for demo
  const handleDemoLogin = async (role) => {
    const credentials = role === 'admin'
      ? { email: 'admin@locksmith.com', password: 'admin123' }
      : { email: 'tech@locksmith.com', password: 'tech123' };
    
    setEmail(credentials.email);
    setPassword(credentials.password);
    
    setLoading(true);
    const result = await login(credentials.email, credentials.password);
    
    if (result.success) {
      toast.success(`Logged in as ${role}!`);
      navigate('/');
    } else {
      toast.error(result.error || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center" data-testid="login-header">
          <div className="mx-auto w-20 h-20 rounded-2xl overflow-hidden bg-primary/5 flex items-center justify-center p-1">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="w-full h-full object-contain" />
            ) : (
              <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
            )}
          </div>
          <div>
            <CardTitle className="text-3xl font-bold" data-testid="login-title">
              {branding?.name || 'My Key Boss'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {branding ? 'Sign in to your account' : 'Locksmith Business Management System'}
            </CardDescription>
            {branding && (
              <p className="text-[10px] text-muted-foreground mt-1">powered by My Key Boss</p>
            )}
          </div>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@locksmith.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  data-testid="password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full btn-gradient-primary"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>

            {/* Demo Login Buttons */}
            <div className="space-y-2 pt-4 border-t">
              <p className="text-sm text-center text-muted-foreground mb-2">Quick Demo Login</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin('admin')}
                  disabled={loading}
                  data-testid="demo-admin-button"
                >
                  Admin Demo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin('tech')}
                  disabled={loading}
                  data-testid="demo-tech-button"
                >
                  Tech Demo
                </Button>
              </div>
            </div>
          </CardContent>
        </form>

        <CardFooter className="flex flex-col space-y-2">
          <Link to="/forgot-password" className="text-xs text-sky-500 hover:underline" data-testid="forgot-password-link">
            Forgot your password?
          </Link>
          <div className="text-sm text-center text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:underline">
              Register here
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
